// Mixpanel for the in-game Help menu (Mel, 2026-09-28): one event scheme for
// every help interaction, so a dashboard can count what players reach for and
// whether it worked. The team-group alert for the same actions is server-side
// (migration 241); this file only feeds Mixpanel.
//
// Events (docs/ops/help-alerts.md has the full list):
//   Help Opened       the "?" sheet opened            { view, entry }
//   Help View Opened  a page inside the sheet opened  { view }
//   Help Action       something was tried             { action, outcome, error_code?, … }
// Every one carries session_id, player_id, is_gm, step_index, step_type,
// screen, session_type.
//
// PII rule (TAS-73): no names, emails, codes or tokens. Extra properties go
// through an allowlist, so a caller can't leak one by accident.
import { track } from '@tasted/utils/analytics';

export const HELP_EVENTS = Object.freeze({
  OPENED: 'Help Opened',
  VIEW_OPENED: 'Help View Opened',
  ACTION: 'Help Action',
});

// Stable snake_case names. Dashboards filter on these: rename = new series.
export const HELP_ACTIONS = Object.freeze({
  MOVE_EVERYONE_ON: 'move_everyone_on',
  RESTART_QUESTION: 'restart_question',
  RESET_GAME: 'reset_game',
  VOTE_OPEN: 'vote_open',
  VOTE_CAST: 'vote_cast',
  VOTE_CANCEL: 'vote_cancel',
  HAND_OVER_GM: 'hand_over_gm',
  MARK_AWAY: 'mark_away',
  LET_BACK_IN: 'let_back_in',
  IM_BACK: 'im_back',
  READMIT_REQUEST: 'readmit_request',
  LATE_JOIN_REQUEST: 'late_join_request',
  READMIT_APPROVE: 'readmit_approve',
  READMIT_DECLINE: 'readmit_decline',
  ISSUE_CODE: 'issue_code',
  REDEEM_HOST_CODE: 'redeem_host_code',
  REDEEM_SEAT_CODE: 'redeem_seat_code',
  WHATSAPP_SUPPORT: 'whatsapp_support',
});

export const HELP_OUTCOMES = Object.freeze({
  STARTED: 'started',
  SUCCEEDED: 'succeeded',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
});

// Extra properties a caller may add. Everything else is dropped.
const EXTRA_KEYS = new Set(['error_code', 'vote_kind', 'choice', 'via', 'server_outcome', 'view', 'entry', 'target_is_self']);

// What the provider knows about this phone (session, seat, GM, step). Hooks
// that live outside the provider (the /join page) pass their own session_id,
// and a context for a different session is ignored.
let context = {};

/** Called by SessionHelpProvider whenever the session, seat or step changes. */
export function setHelpAnalyticsContext(next) {
  context = next && next.session_id ? { ...next } : {};
}

export function getHelpAnalyticsContext() {
  return { ...context };
}

/** The pathname as a screen name: '/game' → 'game', '/join' → 'join'. */
export function screenFromPath(pathname = '') {
  const first = String(pathname).split(/[?#]/)[0].split('/').filter(Boolean)[0];
  return first || 'home';
}

function currentScreen() {
  try {
    return screenFromPath(window.location.pathname);
  } catch {
    return null;
  }
}

/** The base properties every help event carries. */
export function helpBaseProps(overrides = {}) {
  const sessionId = overrides.session_id ?? context.session_id ?? null;
  const ctx = context.session_id && context.session_id === sessionId ? context : {};
  const pick = (key, fallback = null) => (overrides[key] !== undefined ? overrides[key] : ctx[key] ?? fallback);
  const step = pick('step_index');
  return {
    session_id: sessionId,
    player_id: pick('player_id'),
    is_gm: pick('is_gm', false) === true,
    step_index: Number.isInteger(step) ? step : null,
    step_type: pick('step_type'),
    screen: pick('screen') || currentScreen(),
    session_type: pick('session_type'),
  };
}

function extras(extra = {}) {
  const out = {};
  for (const [k, v] of Object.entries(extra || {})) {
    if (EXTRA_KEYS.has(k) && v !== undefined && v !== null && v !== '') out[k] = v;
  }
  return out;
}

/** A Supabase/RPC error, or a failure outcome from utils/rejoinErrors, as a short code. */
export function helpErrorCode(e) {
  if (!e) return null;
  if (typeof e === 'string') return e.slice(0, 40);
  return String(e.code || e.kind || e.reason || 'unknown').slice(0, 40);
}

let seq = 0;

/** The payload of a Help Action event (exported for tests). */
export function helpActionPayload(action, outcome, extra = {}, overrides = {}) {
  return { ...helpBaseProps(overrides), action, outcome, ...extras(extra) };
}

export function trackHelpAction(action, outcome, extra = {}, overrides = {}) {
  try {
    // Each attempt is its own event: the logger's 5 s dedupe must not merge a
    // retry with the first try, or a failure with the success after it.
    track(HELP_EVENTS.ACTION, { ...helpActionPayload(action, outcome, extra, overrides), dedupeKey: `${action}:${outcome}:${++seq}` });
  } catch {
    /* analytics never breaks the help menu */
  }
}

export function trackHelpOpened({ view = 'home', entry = 'button' } = {}, overrides = {}) {
  try {
    track(HELP_EVENTS.OPENED, { ...helpBaseProps(overrides), ...extras({ view, entry }), dedupeKey: `help-opened:${view}:${++seq}` });
  } catch {
    /* ignore */
  }
}

export function trackHelpViewOpened(view, overrides = {}) {
  if (!view || view === 'home') return;
  try {
    track(HELP_EVENTS.VIEW_OPENED, { ...helpBaseProps(overrides), ...extras({ view }), dedupeKey: `help-view:${view}:${++seq}` });
  } catch {
    /* ignore */
  }
}

/**
 * Track `started`, run the call, then `succeeded` or `failed` (with the
 * error code). Returns the call's result untouched. `isOk(result)` decides
 * success; the default is "no .error and .ok isn't false".
 */
export async function withHelpTracking(action, run, { extra = {}, overrides = {}, isOk } = {}) {
  trackHelpAction(action, HELP_OUTCOMES.STARTED, extra, overrides);
  let result;
  try {
    result = await run();
  } catch (err) {
    trackHelpAction(action, HELP_OUTCOMES.FAILED, { ...extra, error_code: helpErrorCode(err) || 'exception' }, overrides);
    throw err;
  }
  const ok = isOk ? isOk(result) : !result?.error && result?.ok !== false;
  trackHelpAction(
    action,
    ok ? HELP_OUTCOMES.SUCCEEDED : HELP_OUTCOMES.FAILED,
    ok
      ? { ...extra, server_outcome: typeof result?.outcome === 'string' ? result.outcome : undefined }
      : { ...extra, error_code: helpErrorCode(result?.error) || result?.outcome || result?.reason || 'unknown' },
    overrides
  );
  return result;
}

/**
 * A tap on a WhatsApp support link. We can't see what happens in WhatsApp, so
 * this is only ever `started`. The chat itself reaches the support number,
 * which the team already reads, so there is no team-group alert for it.
 */
export function trackWhatsAppSupport(via, sessionId) {
  trackHelpAction(HELP_ACTIONS.WHATSAPP_SUPPORT, HELP_OUTCOMES.STARTED, { via }, sessionId ? { session_id: sessionId } : {});
}
